import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Table, Badge, Dropdown } from 'react-bootstrap';
import { FaSearch, FaEye, FaDownload, FaFilter, FaUserTie, FaFilePdf, FaFileExcel, FaMapMarkerAlt } from 'react-icons/fa';
import { clientsAPI } from '../../utils/api';
import { getCurrentUser, hasPermission } from '../../utils/permissionUtils';
import '../../styles/modern-design-system.css';
import './AdminDashboard.css';

const NewClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPersonnel, setFilterPersonnel] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Get current user and check permissions
  const currentUser = getCurrentUser();
  const canExport = currentUser?.role === 'admin' || hasPermission(currentUser, 'newClients', 'export');
  
  // Get unique personnel and sites for filtering
  const uniquePersonnel = [...new Set(clients.map(c => c.personnelName).filter(Boolean))];
  const uniqueSites = [...new Set(clients.map(c => c.siteName).filter(Boolean))];

  // Fetch all clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getAll();
      
      // Handle different response formats
      const clientsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.clients || response.data?.data || []);
      
      setClients(clientsData);
      setError('');
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      
      // Prepare filter parameters
      const filters = {
        personnel: filterPersonnel || undefined,
        site: filterSite || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        search: searchTerm || undefined
      };
      
      const response = await clientsAPI.exportPDF(filters);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      // Prepare filter parameters
      const filters = {
        personnel: filterPersonnel || undefined,
        site: filterSite || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        search: searchTerm || undefined
      };
      
      const response = await clientsAPI.exportExcel(filters);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError('Failed to export Excel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search term and filters
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm) ||
      client.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.personnelName && client.personnelName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesPersonnel = !filterPersonnel || client.personnelName === filterPersonnel;
    const matchesSite = !filterSite || client.siteName === filterSite;
    
    // Date filtering
    let matchesDate = true;
    if (filterDateFrom || filterDateTo) {
      const clientDate = new Date(client.createdAt);
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        matchesDate = matchesDate && clientDate >= fromDate;
      }
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && clientDate <= toDate;
      }
    }
    
    return matchesSearch && matchesPersonnel && matchesSite && matchesDate;
  });

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-2" style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-light))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                New Clients
              </h1>
              <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                View all clients submitted by personnel • {filteredClients.length} {filteredClients.length === 1 ? 'client' : 'clients'}
              </p>
            </div>
            <Dropdown>
              <Dropdown.Toggle 
                variant="primary"
                className="d-flex align-items-center gap-2"
                style={{
                  padding: '0.625rem 1.5rem',
                  fontWeight: 600
                }}
                disabled={!canExport || filteredClients.length === 0}
                title={!canExport ? "You don't have permission to export" : ""}
              >
                <FaDownload />
                Export Report
              </Dropdown.Toggle>

              <Dropdown.Menu 
                align="end"
                style={{
                  background: 'var(--surface-glass)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '0.5rem',
                  minWidth: '200px'
                }}
              >
                <Dropdown.Item
                  onClick={handleExportPDF}
                  style={{
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <FaFilePdf style={{ color: '#dc3545', fontSize: '1.25rem' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Export as PDF</div>
                    <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      Professional report format
                    </small>
                  </div>
                </Dropdown.Item>
                
                <Dropdown.Divider style={{ margin: '0.5rem 0', borderColor: 'var(--border-color)' }} />
                
                <Dropdown.Item
                  onClick={handleExportExcel}
                  style={{
                    color: 'var(--text-primary)',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <FaFileExcel style={{ color: '#28a745', fontSize: '1.25rem' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Export as Excel</div>
                    <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      Spreadsheet with all data
                    </small>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card className="shadow-sm mb-4" style={{
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-xl)'
      }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <Form.Control
                  type="text"
                  placeholder="Search by name, phone, site, or personnel..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '2.5rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </Col>
          </Row>
          
          <Row className="g-3 mt-2">
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Personnel
              </Form.Label>
              <Form.Select
                value={filterPersonnel}
                onChange={(e) => setFilterPersonnel(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="">All Personnel</option>
                {uniquePersonnel.map((personnel, index) => (
                  <option key={index} value={personnel}>
                    {personnel}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Site
              </Form.Label>
              <Form.Select
                value={filterSite}
                onChange={(e) => setFilterSite(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="">All Sites</option>
                {uniqueSites.map((site, index) => (
                  <option key={index} value={site}>
                    {site}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date From
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date To
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
          </Row>
          
          {(filterPersonnel || filterSite || filterDateFrom || filterDateTo) && (
            <div className="mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setFilterPersonnel('');
                  setFilterSite('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Clients Table */}
      <Card className="shadow-sm" style={{
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-xl)'
      }}>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
              <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Loading clients...</p>
            </div>
          ) : filteredClients.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="modern-table">
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Phone</th>
                    <th className="d-none d-lg-table-cell">Site</th>
                    <th className="d-none d-md-table-cell">Added By</th>
                    <th className="d-none d-xl-table-cell">Date Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {client.name}
                          </div>
                          <div className="d-lg-none" style={{ 
                            fontSize: 'var(--font-size-xs)', 
                            color: 'var(--text-muted)',
                            marginTop: '0.25rem'
                          }}>
                            {client.siteName}
                          </div>
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {client.phone}
                      </td>
                      <td className="d-none d-lg-table-cell" style={{ color: 'var(--text-secondary)' }}>
                        {client.siteName}
                      </td>
                      <td className="d-none d-md-table-cell">
                        <div className="d-flex align-items-center gap-2">
                          <FaUserTie style={{ color: 'var(--accent-blue)' }} />
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {client.personnelName || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="d-none d-xl-table-cell" style={{ color: 'var(--text-secondary)' }}>
                        {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetails(client)}
                          title="View Details"
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <FaUserTie size={64} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
              <p className="mt-3" style={{ color: 'var(--text-muted)' }}>
                {searchTerm || filterPersonnel || filterSite 
                  ? 'No clients found matching your filters.' 
                  : 'No clients have been added yet.'}
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Client Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton style={{
          background: 'var(--surface-glass)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Modal.Title style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
            <FaUserTie className="me-2" />
            Client Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--bg-primary)', padding: '2rem' }}>
          {selectedClient && (
            <Row>
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Client Name
                  </label>
                  <p style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.name}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Phone Number
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.phone}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Contact Person
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.contactPerson || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No contact person provided</span>}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Contact Person Phone
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.contactPhone || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No contact phone provided</span>}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Branch
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.branch || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No branch provided</span>}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Region
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.region || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No region provided</span>}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Site
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.siteName}
                  </p>
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Physical Location
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.physicalLocation || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No location provided</span>}
                  </p>
                  {(selectedClient.locationName || selectedClient.locationCoords) && (
                    <div className="mt-2 p-2" style={{
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--accent-blue)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.85rem'
                    }}>
                      {selectedClient.locationName && (
                        <div style={{ color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                          <FaMapMarkerAlt style={{ color: 'var(--accent-blue)', marginRight: '0.4rem' }} />
                          {selectedClient.locationName}
                        </div>
                      )}
                      {selectedClient.locationCoords && (
                        <div style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          📍 {selectedClient.locationCoords}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Col>
              
              <Col md={6} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Added By
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    <FaUserTie className="me-2" style={{ color: 'var(--accent-blue)' }} />
                    {selectedClient.personnelName || 'Unknown'}
                  </p>
                </div>
              </Col>
              
              <Col xs={12} className="mb-4">
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Description
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.description || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No description provided</span>}
                  </p>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Date Added
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="p-3" style={{
                  background: 'var(--surface-glass)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: 'var(--text-muted)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Last Updated
                  </label>
                  <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                    {selectedClient.updatedAt ? new Date(selectedClient.updatedAt).toLocaleString() : 'Not updated'}
                  </p>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          background: 'var(--surface-glass)',
          borderTop: '1px solid var(--border-color)'
        }}>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NewClients;
